'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { OptionPicker, type OptionPickerOption } from '@kit/ui/option-picker';

import { getAllCountryOptions, type CountryOption } from '../../lib/countries';

interface CountrySelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Optional custom country list. If not provided, uses complete ISO 3166-1 list */
  countries?: Array<{ country_code: string; country_name: string }>;
  disabled?: boolean;
  /**
   * Optional context slot rendered above the search field (desktop) and above
   * the sheet search input (mobile). Forwarded verbatim to the primitive.
   */
  header?: ReactNode;
  /**
   * Trigger placeholder shown when nothing is selected. When omitted, falls
   * back to the `common:selectCountry` translation — supply an explicit string
   * on surfaces that don't load the `dancers`/`common` namespaces.
   */
  placeholder?: string;
  /**
   * Placeholder and accessible name of the search box inside the popover.
   * When omitted, falls back to the `common:selectCountry` translation.
   */
  searchPlaceholder?: string;
  /**
   * Validation error message. Forwarded to the underlying `OptionPicker` so
   * the field renders its error-border affordance and message.
   */
  error?: string;
  /** Input id, so an external `<Label htmlFor>` or `FormControl` reaches it. */
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Single-country picker. Thin wrapper over `@kit/ui/option-picker`, sourcing
 * the full ISO 3166-1 list (+ flags) from `getAllCountryOptions`.
 *
 * BAL-1132: this used to wrap `Autocomplete`, whose trigger was a text input.
 * Chrome autofilled "Germany" into it on the contract wizard's Tax Information
 * step; the field showed Germany while `tax.countryCode` stayed '' and the
 * wizard would not continue. The OptionPicker trigger is a button that shows
 * only the committed country, and search happens inside the popover.
 *
 * Canonical home (`@kit/artists`) — the package that owns the country dataset.
 * Consumed by the dancer profile wizard, contract details, and the competition
 * application form.
 */
export function CountrySelector({
  value,
  onValueChange,
  countries: customCountries,
  disabled = false,
  placeholder,
  searchPlaceholder,
  error,
  header,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: CountrySelectorProps) {
  const { t } = useTranslation('dancers');

  // Use complete country list if no custom list provided
  const options = useMemo<OptionPickerOption[]>(() => {
    const source =
      customCountries && customCountries.length > 0
        ? customCountries
        : getAllCountryOptions();
    return source.map((country) => {
      const flag =
        'flag' in country ? (country as CountryOption).flag : undefined;
      return {
        value: country.country_code,
        label: `${country.country_name} (${country.country_code})`,
        icon: flag ? <span>{flag}</span> : undefined,
      };
    });
  }, [customCountries]);

  return (
    <OptionPicker
      // Option values are uppercase ISO codes; normalize the incoming value so a
      // legacy lowercase-stored code ("us") still resolves to its option ("US")
      // instead of rendering blank. Re-saving then persists the canonical code.
      value={value ? value.toUpperCase() : null}
      onChange={onValueChange}
      options={options}
      disabled={disabled}
      header={header}
      // The previous picker could always be cleared; optional country fields
      // (organisation locations, competition residence) rely on it.
      clearable
      clearLabel={t('common:clearSelection')}
      placeholder={placeholder ?? t('common:selectCountry')}
      searchPlaceholder={searchPlaceholder ?? t('common:selectCountry')}
      emptyMessage={t('common:noResultsFound')}
      aria-label={placeholder ?? t('common:selectCountry')}
      error={error}
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      // No autoSelectOnBlur (dev #3102 set it on the Autocomplete this
      // replaces): the search text is never a value, so "Germany" + click away
      // commits nothing; Enter or a click on the row picks it. Antoine's
      // decision on #2878, 2026-10-11.
    />
  );
}
